const apiUrl = process.env.REACT_APP_API_URL;

export interface MongoDbMeta {
    name: string;
    actualName?: string;
    isSystem?: boolean;
    isOwn?: boolean;
    isStudentDb?: boolean;
    studentName?: string;
    studentId?: number;
    aulaName?: string;
    readOnly?: boolean;
    collections?: string[];
}

export interface MongoDbDetail {
    name: string;
    collections: string[];
}

export interface MongoExecuteResponse {
    database: string;
    type: 'documents' | 'write' | 'command' | 'error';
    data?: any;
    count?: number;
    error?: string;
    durationMs?: number;
}

export interface MongoEvaluateResponse {
    correct: boolean;
    passed?: boolean;
    status?: string;
    feedback?: string;
    expectedPreview?: string;
    actualPreview?: string;
    error?: string;
}

export interface MongoFileItem {
    name: string;
    path: string;
    isDir?: boolean;
}

function getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export const MongoIdeApi = {
    // ---- Bases de dades NoSQL / MongoDB ----
    async listDatabases(): Promise<MongoDbMeta[]> {
        const res = await fetch(`${apiUrl}:8061/bbdd/mongo-ide/databases`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error(await res.text());
        return await res.json();
    },

    async createDatabase(name: string): Promise<any> {
        const res = await fetch(`${apiUrl}:8061/bbdd/mongo-ide/databases`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ name })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Error creant base de dades MongoDB' }));
            throw new Error(err.error || 'Error creant base de dades MongoDB');
        }
        return await res.json();
    },

    async dropDatabase(name: string): Promise<void> {
        const res = await fetch(`${apiUrl}:8061/bbdd/mongo-ide/databases/${encodeURIComponent(name)}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Error eliminant base de dades MongoDB' }));
            throw new Error(err.error || 'Error eliminant base de dades MongoDB');
        }
    },

    async describeDatabase(name: string): Promise<MongoDbDetail> {
        const res = await fetch(`${apiUrl}:8061/bbdd/mongo-ide/databases/${encodeURIComponent(name)}/describe`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error(await res.text());
        return await res.json();
    },

    async getDocuments(dbName: string, colName: string, limit: number = 50): Promise<any[]> {
        const res = await fetch(`${apiUrl}:8061/bbdd/mongo-ide/databases/${encodeURIComponent(dbName)}/collections/${encodeURIComponent(colName)}/documents?limit=${limit}`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error(await res.text());
        return await res.json();
    },

    // ---- Execució de queries MongoDB ----
    async executeMongoQuery(database: string, script: string): Promise<MongoExecuteResponse> {
        const res = await fetch(`${apiUrl}:8061/bbdd/mongo-ide/execute`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ database, script })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Error executant query MongoDB' }));
            throw new Error(err.error || 'Error executant query MongoDB');
        }
        return await res.json();
    },

    // ---- Avaluació de problemes NoSQL (Jutge) ----
    async evaluateMongoExercise(idProblema: string | number, query: string): Promise<MongoEvaluateResponse> {
        const res = await fetch(`${apiUrl}:8061/bbdd/mongo-ide/evaluate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ idProblema: String(idProblema), query })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Error avaluant exercici NoSQL' }));
            throw new Error(err.error || 'Error avaluant exercici NoSQL');
        }
        return await res.json();
    },

    // ---- Fitxers persistents de scripts (.js / .mongodb / .json) ----
    async listFiles(targetStudentId?: number): Promise<MongoFileItem[]> {
        const url = targetStudentId
            ? `${apiUrl}:8061/bbdd/mongo-ide/files?targetStudentId=${targetStudentId}`
            : `${apiUrl}:8061/bbdd/mongo-ide/files`;
        const res = await fetch(url, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error(await res.text());
        return await res.json();
    },

    async readFile(name: string, targetStudentId?: number): Promise<string> {
        const queryParams = new URLSearchParams({ path: name });
        if (targetStudentId) queryParams.set('targetStudentId', String(targetStudentId));

        let res = await fetch(`${apiUrl}:8061/bbdd/mongo-ide/files/content?${queryParams.toString()}`, {
            headers: getAuthHeaders()
        });

        if (!res.ok) {
            const url = targetStudentId
                ? `${apiUrl}:8061/bbdd/mongo-ide/files/${encodeURIComponent(name)}?targetStudentId=${targetStudentId}`
                : `${apiUrl}:8061/bbdd/mongo-ide/files/${encodeURIComponent(name)}`;
            res = await fetch(url, { headers: getAuthHeaders() });
        }

        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        return data.content ?? '';
    },

    async saveFile(name: string, content: string): Promise<void> {
        // Enviar mitjançant POST /bbdd/mongo-ide/files amb { path, content }
        const res = await fetch(`${apiUrl}:8061/bbdd/mongo-ide/files`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ path: name, content })
        });
        if (!res.ok) {
            // Intent fallback per PUT /bbdd/mongo-ide/files/{name}
            const putRes = await fetch(`${apiUrl}:8061/bbdd/mongo-ide/files/${encodeURIComponent(name)}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify({ content })
            });
            if (!putRes.ok) {
                const err = await putRes.json().catch(() => ({ error: 'Error desant fitxer de script' }));
                throw new Error(err.error || 'Error desant fitxer de script');
            }
        }
    },

    async deleteFile(name: string): Promise<void> {
        const res = await fetch(`${apiUrl}:8061/bbdd/mongo-ide/files/${encodeURIComponent(name)}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Error eliminant fitxer de script' }));
            throw new Error(err.error || 'Error eliminant fitxer de script');
        }
    }
};
